import * as React from "react";
import { CircleAlert } from "lucide-react";

import { cn } from "../lib/utils";
import { Icon } from "./icon";
import { Label } from "./label";
import { InfoTooltip } from "./tooltip";

/**
 * v2 Field (redesign phase 5, #455): label + control + hint + error, with an
 * optional InfoTooltip and character counter. Primitive-level: plain props,
 * no form library. A react-hook-form wrapper passes `fieldState.error?.message`
 * as `error` (see docs/design-library/04-implementation-notes.md).
 *
 * Ids are wired through context: Input and Textarea (and later controls) call
 * `useFieldControl`, which supplies `id`, `aria-describedby`, `aria-invalid`,
 * `aria-required` and `disabled`. Set the id on the Field, not the control,
 * or the label loses its target.
 *
 * Hint and error sit below the control at 12px; an error replaces the hint
 * (break #45), so `aria-describedby` points at whichever is shown, plus the
 * counter. The error is not a live region: announcing on submit (focusing the
 * first invalid field) is the form's job.
 *
 * Layouts: `vertical` (label above); `horizontal` (label left, control right,
 * two equal columns, or `controlWidth="fit"` for a switch row: label column
 * fills, control at the right edge); `inline` (Checkbox: control first, label
 * Regular to its right, break #54). In `fit` and `inline` the control sits in a
 * 20px cell centred on the label's first line.
 */

type FieldContextValue = {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
};

const FieldContext = React.createContext<FieldContextValue | null>(null);

type ControlProps = {
  id?: string;
  disabled?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-required"?: React.AriaAttributes["aria-required"];
};

/**
 * Merges a control's props with the surrounding Field. Explicit props win;
 * `aria-describedby` is joined. Outside a Field it returns the props unchanged.
 */
export function useFieldControl<P extends ControlProps>(props: P): P {
  const field = React.useContext(FieldContext);
  if (!field) return props;
  const describedBy = [field.describedBy, props["aria-describedby"]].filter(Boolean).join(" ");
  return {
    ...props,
    id: props.id ?? field.id,
    disabled: props.disabled ?? (field.disabled || undefined),
    "aria-describedby": describedBy || undefined,
    "aria-invalid": props["aria-invalid"] ?? (field.invalid || undefined),
    // aria-required, not native `required`: the browser's validation bubble is unstyled and often English.
    "aria-required": props["aria-required"] ?? (field.required || undefined),
  };
}

const formatNumber = (n: number) => n.toLocaleString("de-DE");

/** "120 / 500"; without a maximum just the count. */
export function defaultFormatCount(count: number, max?: number): string {
  return max === undefined ? formatNumber(count) : `${formatNumber(count)} / ${formatNumber(max)}`;
}

/**
 * The error line under a control: 12px, `--destructive-accent`, CircleAlert.
 * Field renders it; controls that own their message (Dropzone) reuse it and
 * point their `aria-describedby` at its id.
 */
export function FieldError({ id, className, children }: { id: string; className?: string; children: React.ReactNode }) {
  return (
    <p id={id} className={cn("flex items-start gap-1 font-body text-caption text-destructive-accent", className)}>
      {/* 12px icon on a 16px line: centred on the first line by the 2px inset. */}
      <Icon icon={CircleAlert} size={12} className="mt-0.5" />
      <span>{children}</span>
    </p>
  );
}

export interface FieldProps {
  label: React.ReactNode;
  /** Below the control, 12px muted. Hidden while `error` is set. */
  hint?: React.ReactNode;
  /** Below the control in the error colour; sets `aria-invalid` on the control. */
  error?: React.ReactNode;
  /** InfoTooltip text next to the label. Text only. */
  info?: React.ReactNode;
  /** Accessible name of the info icon. German UI text. */
  infoLabel?: string;
  /** Marks the label "(optional)". */
  optional?: boolean;
  optionalText?: string;
  /** Sets `aria-required` on the control. No visual mark. */
  required?: boolean;
  /** Current length; shows the counter. */
  count?: number;
  maxCount?: number;
  formatCount?: (count: number, max?: number) => string;
  disabled?: boolean;
  /**
   * `horizontal`: label (+ hint) left, control right; with `controlWidth="half"` the label is aligned to a
   * 32px (md) control. `inline`: control left, label (+ hint, error) right, for Checkbox.
   */
  orientation?: "vertical" | "horizontal" | "inline";
  /** Horizontal only. `half`: two equal columns (inputs). `fit`: the control takes its own width at the right edge (switches). */
  controlWidth?: "half" | "fit";
  /** Id of the control; generated when omitted. */
  id?: string;
  className?: string;
  children: React.ReactNode;
}

function Field({
  label,
  hint,
  error,
  info,
  infoLabel = "Mehr Informationen",
  optional = false,
  optionalText,
  required = false,
  count,
  maxCount,
  formatCount = defaultFormatCount,
  disabled = false,
  orientation = "vertical",
  controlWidth = "half",
  id: idProp,
  className,
  children,
}: FieldProps) {
  const generated = React.useId();
  const id = idProp ?? generated;
  const hasError = error != null && error !== false && error !== "";
  const showHint = !hasError && hint != null && hint !== false && hint !== "";
  const showCount = count !== undefined;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const countId = `${id}-count`;

  const describedBy =
    [hasError ? errorId : showHint ? hintId : undefined, showCount ? countId : undefined].filter(Boolean).join(" ") ||
    undefined;

  const context = React.useMemo<FieldContextValue>(
    () => ({ id, describedBy, invalid: hasError, required, disabled }),
    [id, describedBy, hasError, required, disabled],
  );

  const labelRow = (
    <div className="flex items-center gap-1.5">
      <Label
        htmlFor={id}
        optional={optional}
        optionalText={optionalText}
        disabled={disabled}
        // An inline label reads as option text, not a field heading (break #54).
        className={orientation === "inline" ? "font-normal" : undefined}
      >
        {label}
      </Label>
      {info != null && <InfoTooltip label={infoLabel}>{info}</InfoTooltip>}
    </div>
  );

  const hintNode = showHint ? (
    <p id={hintId} className="font-body text-caption text-ink-muted">
      {hint}
    </p>
  ) : null;

  const errorNode = hasError ? <FieldError id={errorId}>{error}</FieldError> : null;

  const countNode = showCount ? (
    <p
      id={countId}
      className={cn(
        "ml-auto shrink-0 font-sans text-caption tabular-nums",
        maxCount !== undefined && count > maxCount ? "text-destructive-accent" : "text-ink-muted",
      )}
    >
      {formatCount(count, maxCount)}
    </p>
  ) : null;

  const below = (message: React.ReactNode) =>
    message || countNode ? (
      <div className="flex items-start gap-3">
        {message && <div className="min-w-0 flex-1">{message}</div>}
        {countNode}
      </div>
    ) : null;

  // `fit` and `inline`: a 20px cell centres a small control (16px box, 18px switch) on the label's first line.
  const controlCell = <div className="flex min-h-5 shrink-0 items-center">{children}</div>;

  if (orientation === "inline") {
    return (
      <FieldContext.Provider value={context}>
        <div className={cn("flex items-start gap-2", className)}>
          {controlCell}
          <div className="flex min-w-0 flex-col gap-1">
            {labelRow}
            {below(errorNode ?? hintNode)}
          </div>
        </div>
      </FieldContext.Provider>
    );
  }

  if (orientation === "horizontal" && controlWidth === "fit") {
    return (
      <FieldContext.Provider value={context}>
        <div className={cn("flex items-start gap-4", className)}>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {labelRow}
            {below(errorNode ?? hintNode)}
          </div>
          {controlCell}
        </div>
      </FieldContext.Provider>
    );
  }

  if (orientation === "horizontal") {
    return (
      <FieldContext.Provider value={context}>
        <div className={cn("grid grid-cols-2 items-start gap-x-4", className)}>
          {/* pt-1.5: the 20px label line centred on a 32px control. */}
          <div className="flex flex-col gap-1 pt-1.5">
            {labelRow}
            {hintNode}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            {children}
            {below(errorNode)}
          </div>
        </div>
      </FieldContext.Provider>
    );
  }

  return (
    <FieldContext.Provider value={context}>
      <div className={cn("flex flex-col gap-2", className)}>
        {labelRow}
        {children}
        {below(errorNode ?? hintNode)}
      </div>
    </FieldContext.Provider>
  );
}

export { Field };
